/**
 * DeleteSessionDialog - Confirmation dialog for permanently deleting a session
 */

import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useDeleteSession } from '@/hooks/useSessions'

interface DeleteSessionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
  sessionTitle: string
}

export function DeleteSessionDialog({
  open,
  onOpenChange,
  sessionId,
  sessionTitle,
}: DeleteSessionDialogProps) {
  const { t } = useTranslation()
  const deleteSession = useDeleteSession()

  const handleDelete = async () => {
    try {
      await deleteSession.mutateAsync(sessionId)
      onOpenChange(false)
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-destructive">
            {t('session.delete.title')}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-neutral-900 block mb-2">
              "{sessionTitle || t('session.newConversation')}"
            </span>
            {t('session.delete.confirm')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteSession.isPending}
          >
            {deleteSession.isPending ? (
              <Spinner className="h-4 w-4 mr-2" />
            ) : null}
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
